import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchInventoryEvents,
  selectInventoryEvents,
  selectInventoryEventsLoading,
  selectInventoryEventsError,
  selectInventoryEventsFilters,
  setFilters,
  clearFilters
} from '../store/slices/inventoryEventsSlice';
import { fetchProducts, selectAllProducts } from '../store/slices/productsSlice';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Calendar,
  Package,
  MapPin,
  TrendingUp,
  TrendingDown,
  Filter as FilterIcon,
  X,
  AlertCircle
} from 'lucide-react';
import { format, startOfWeek, endOfWeek, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import './InventoryHistory.css';

const InventoryHistory = () => {
  const dispatch = useDispatch();
  const events = useSelector(selectInventoryEvents);
  const products = useSelector(selectAllProducts);
  const loading = useSelector(selectInventoryEventsLoading);
  const error = useSelector(selectInventoryEventsError);
  const filters = useSelector(selectInventoryEventsFilters);

  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    dispatch(fetchProducts());
    
    // Cargar eventos de la última semana por defecto
    const today = new Date();
    const weekStart = startOfWeek(today, { locale: es });
    const weekEnd = endOfWeek(today, { locale: es });
    
    dispatch(setFilters({
      fecha_desde: weekStart.toISOString(),
      fecha_hasta: weekEnd.toISOString()
    }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchInventoryEvents(filters));
  }, [dispatch, filters]);

  // Datos para el gráfico de eventos por día
  const eventsByDay = useMemo(() => {
    if (!events || events.length === 0) return [];

    const grouped = events.reduce((acc, event) => {
      const date = format(parseISO(event.fecha_evento), 'dd/MM', { locale: es });
      
      if (!acc[date]) {
        acc[date] = { date, agregar: 0, rebajar: 0 };
      }

      if (event.tipo_evento === 'AGREGAR') {
        acc[date].agregar += event.cantidad;
      } else if (event.tipo_evento === 'REBAJAR') {
        acc[date].rebajar += event.cantidad;
      }

      return acc;
    }, {});

    return Object.values(grouped).sort((a, b) => {
      const [dayA, monthA] = a.date.split('/');
      const [dayB, monthB] = b.date.split('/');
      return new Date(2024, monthA - 1, dayA) - new Date(2024, monthB - 1, dayB);
    });
  }, [events]);

  // Datos para el gráfico de inventario por producto
  const inventoryByProduct = useMemo(() => {
    if (!events || events.length === 0) return [];

    const grouped = events.reduce((acc, event) => {
      const productName = event.producto?.nombre || 'Desconocido';
      
      if (!acc[productName]) {
        acc[productName] = { producto: productName, cantidad: 0 };
      }

      if (event.tipo_evento === 'AGREGAR') {
        acc[productName].cantidad += event.cantidad;
      } else if (event.tipo_evento === 'REBAJAR') {
        acc[productName].cantidad -= event.cantidad;
      }

      return acc;
    }, {});

    return Object.values(grouped)
      .sort((a, b) => b.cantidad - a.cantidad)
      .slice(0, 10); // Top 10 productos
  }, [events]);

  // Datos para el gráfico de tipo de eventos
  const eventsByType = useMemo(() => {
    if (!events || events.length === 0) return [];

    const agregar = events.filter(e => e.tipo_evento === 'AGREGAR').length;
    const venta = events.filter(e => e.tipo_rebaja === 'VENTA').length;
    const merma = events.filter(e => e.tipo_rebaja === 'MERMA').length;

    return [
      { name: 'Agregar', value: agregar, color: '#10B981' },
      { name: 'Venta', value: venta, color: '#DC2626' },
      { name: 'Merma', value: merma, color: '#F59E0B' }
    ].filter(item => item.value > 0);
  }, [events]);

  const handleFilterChange = (key, value) => {
    dispatch(setFilters({ [key]: value }));
  };

  const handleClearFilters = () => {
    const today = new Date();
    const weekStart = startOfWeek(today, { locale: es });
    const weekEnd = endOfWeek(today, { locale: es });
    
    dispatch(clearFilters());
    dispatch(setFilters({
      fecha_desde: weekStart.toISOString(),
      fecha_hasta: weekEnd.toISOString()
    }));
  };

  const totalAgregar = useMemo(() => 
    events.filter(e => e.tipo_evento === 'AGREGAR')
      .reduce((sum, e) => sum + e.cantidad, 0),
    [events]
  );

  const totalRebajar = useMemo(() => 
    events.filter(e => e.tipo_evento === 'REBAJAR')
      .reduce((sum, e) => sum + e.cantidad, 0),
    [events]
  );

  if (error) {
    return (
      <div className="inventory-history-container">
        <div className="error-message">
          <AlertCircle size={24} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="inventory-history-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <Calendar size={32} />
            Historial de Inventario
          </h1>
          <p className="page-subtitle">
            Visualiza los movimientos de inventario y estadísticas
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-header">
          <button
            className="btn-toggle-filters"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterIcon size={20} />
            Filtros
          </button>
          {(filters.ubicacion !== 'TODOS' || filters.producto_id) && (
            <button className="btn-clear-filters" onClick={handleClearFilters}>
              <X size={16} />
              Limpiar filtros
            </button>
          )}
        </div>

        {showFilters && (
          <div className="filters-content">
            {/* Ubicación */}
            <div className="filter-group">
              <label>
                <MapPin size={16} />
                Ubicación
              </label>
              <select
                value={filters.ubicacion}
                onChange={(e) => handleFilterChange('ubicacion', e.target.value)}
              >
                <option value="TODOS">Todos</option>
                <option value="Campo">Campo</option>
                <option value="Warehouse USA">Warehouse USA</option>
              </select>
            </div>

            {/* Producto */}
            <div className="filter-group">
              <label>
                <Package size={16} />
                Producto
              </label>
              <select
                value={filters.producto_id || ''}
                onChange={(e) => handleFilterChange('producto_id', e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Todos los productos</option>
                {products.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.nombre}
                  </option>
                ))}
              </select>
            </div>

            {/* Fecha desde */}
            <div className="filter-group">
              <label>
                <Calendar size={16} />
                Desde
              </label>
              <input
                type="date"
                value={filters.fecha_desde ? format(parseISO(filters.fecha_desde), 'yyyy-MM-dd') : ''}
                onChange={(e) => handleFilterChange('fecha_desde', e.target.value ? new Date(e.target.value).toISOString() : null)}
              />
            </div>

            {/* Fecha hasta */}
            <div className="filter-group">
              <label>
                <Calendar size={16} />
                Hasta
              </label>
              <input
                type="date"
                value={filters.fecha_hasta ? format(parseISO(filters.fecha_hasta), 'yyyy-MM-dd') : ''}
                onChange={(e) => handleFilterChange('fecha_hasta', e.target.value ? new Date(e.target.value).toISOString() : null)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-agregar">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Agregado</p>
            <p className="stat-value">{totalAgregar.toLocaleString()}</p>
          </div>
        </div>

        <div className="stat-card stat-rebajar">
          <div className="stat-icon">
            <TrendingDown size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Rebajado</p>
            <p className="stat-value">{totalRebajar.toLocaleString()}</p>
          </div>
        </div>

        <div className="stat-card stat-events">
          <div className="stat-icon">
            <Calendar size={24} />
          </div>
          <div className="stat-content">
            <p className="stat-label">Total Eventos</p>
            <p className="stat-value">{events.length}</p>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Cargando eventos...</p>
        </div>
      ) : events.length === 0 ? (
        <div className="empty-state">
          <Calendar size={48} />
          <p>No hay eventos en el período seleccionado</p>
        </div>
      ) : (
        <>
          {/* Charts Grid */}
          <div className="charts-grid">
            {/* Eventos por día */}
            <div className="chart-card">
              <h3 className="chart-title">Movimientos por Día</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={eventsByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="agregar" fill="#10B981" name="Agregado" />
                  <Bar dataKey="rebajar" fill="#DC2626" name="Rebajado" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Inventario por producto */}
            <div className="chart-card">
              <h3 className="chart-title">Top 10 Productos (Movimiento Neto)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={inventoryByProduct} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="producto" type="category" width={150} />
                  <Tooltip />
                  <Bar dataKey="cantidad" fill="#2c5f2d" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Distribución de eventos */}
            {eventsByType.length > 0 && (
              <div className="chart-card">
                <h3 className="chart-title">Distribución de Eventos</h3>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={eventsByType}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {eventsByType.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>

          {/* Events Table */}
          <div className="events-table-card">
            <h3 className="table-title">Detalle de Eventos</h3>
            <div className="table-container">
              <table className="events-table">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Producto</th>
                    <th>Tipo</th>
                    <th>Cantidad</th>
                    <th>Ubicación</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((event) => (
                    <tr key={event.id}>
                      <td>
                        {format(parseISO(event.fecha_evento), 'dd/MM/yyyy HH:mm', { locale: es })}
                      </td>
                      <td>
                        <strong>{event.producto?.nombre || 'N/A'}</strong>
                      </td>
                      <td>
                        <span className={`badge badge-${event.tipo_evento.toLowerCase()}`}>
                          {event.tipo_evento === 'AGREGAR' ? 'Agregar' : 
                           event.tipo_rebaja === 'VENTA' ? 'Venta' : 'Merma'}
                        </span>
                      </td>
                      <td className={event.tipo_evento === 'AGREGAR' ? 'text-success' : 'text-danger'}>
                        {event.tipo_evento === 'AGREGAR' ? '+' : '-'}{event.cantidad}
                      </td>
                      <td>{event.ubicacion}</td>
                      <td className="text-muted">{event.observaciones || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InventoryHistory;
